import { TaapMetricValue } from '../types/taap';

/**
 * 通貨表記。JPY は小数を持たないので桁を出さない。
 */
export const formatMoney = (value: number | null | undefined, currency = 'JPY'): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const symbols: Record<string, string> = { JPY: '¥', USD: '$', EUR: '€', GBP: '£' };
  const isZeroDecimal = currency === 'JPY';
  const formatted = value.toLocaleString('ja-JP', {
    minimumFractionDigits: 0,
    maximumFractionDigits: isZeroDecimal ? 0 : 2,
  });
  return `${symbols[currency] ?? ''}${formatted}`;
};

export const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return value.toLocaleString('ja-JP');
};

export const formatPercent = (value: number | null | undefined, digits = 1): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${value.toFixed(digits)}%`;
};

/**
 * CV が 0 の行では CPA も ROAS も計算できない。
 *
 * バックエンドは計算不能を 0 で返すため、そのまま出すと「獲得単価 ¥0」＝
 * 無料で獲得できたように読めてしまう。分母が無いことを「—」で示す。
 */
export const formatRatio = (
  value: number | null | undefined,
  denominator: number,
  format: (value: number) => string,
): string => (denominator > 0 && value ? format(value) : '—');

/**
 * 前期間比の表示。前期間にデータが無い場合は「—」を返す。
 * 0 からの増加を +100% と書くと実態より良く見えるため。
 */
export const formatChange = (metric: TaapMetricValue | null | undefined): string => {
  if (!metric || metric.change_rate === null) return '前期間データなし';
  const sign = metric.change_rate > 0 ? '+' : '';
  return `${sign}${metric.change_rate.toFixed(1)}%`;
};

/**
 * 変化が良い方向かどうか。CPA のように「低いほど良い」指標があるため、
 * 増減の符号だけで色を決められない。
 */
export const isImprovement = (
  metricKey: string,
  changeRate: number | null | undefined,
): boolean | null => {
  if (changeRate === null || changeRate === undefined || changeRate === 0) return null;
  const lowerIsBetter = metricKey === 'cpa' || metricKey === 'cost';
  return lowerIsBetter ? changeRate < 0 : changeRate > 0;
};

/** 「5分前」「3時間前」。同期の鮮度は相対表記の方が判断しやすい。 */
export const formatRelativeTime = (iso: string | null | undefined): string => {
  if (!iso) return '未同期';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '未同期';

  const diffMinutes = Math.floor((Date.now() - then) / 60000);
  if (diffMinutes < 1) return 'たった今';
  if (diffMinutes < 60) return `${diffMinutes}分前`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}時間前`;

  return `${Math.floor(diffHours / 24)}日前`;
};

export const PLATFORM_LABELS: Record<string, string> = {
  meta: 'Meta',
  google: 'Google 広告',
  yahoo: 'Yahoo!広告',
  x: 'X広告',
};

export const PROPOSAL_TYPE_LABELS: Record<string, string> = {
  creative_refresh: '表現の差替（L1）',
  appeal_change: '訴求の変更（L3）',
  budget_shift: '予算配分',
  experiment: '実験（探索）',
};

export const PRIORITY_LABELS: Record<string, string> = {
  high: '優先度: 高',
  medium: '優先度: 中',
  low: '優先度: 低',
};
